import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { getDoctorAvatar, cleanupImageUrl, getGenderAvatar } from '../../utils/imageUtils';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const DoctorCard = ({ 
  doctor, 
  variant = 'default', // 'default', 'home', 'department'
  showActions = true,
  onClick 
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  // Load image from database with gender fallback
  useEffect(() => {
    const loadDoctorImage = async () => {
      setImageLoading(true);
      try {
        const avatarUrl = await getDoctorAvatar(doctor);
        setImageUrl(avatarUrl);
      } catch (error) {
        console.error('Error loading doctor image:', error);
        setImageUrl(getGenderAvatar(doctor.gender));
      }
      setImageLoading(false);
    };

    loadDoctorImage();

    // Cleanup function
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        cleanupImageUrl(imageUrl);
      }
    };
  }, [doctor.image, doctor.imageId, doctor.image_id, doctor.gender]);

  const formatPracticeSchedule = (doctor) => {
    if (!doctor.practiceDays || doctor.practiceDays.length === 0) {
      return 'Schedule not specified';
    }
    
    // Use detailed schedule if available, otherwise just show days
    if (doctor.detailedSchedule && Object.keys(doctor.detailedSchedule).length > 0) {
      const scheduleText = [];
      doctor.practiceDays.forEach(day => {
        const daySchedule = doctor.detailedSchedule[day];
        if (daySchedule && (daySchedule.AM || daySchedule.PM)) {
          scheduleText.push(day);
        }
      });
      return scheduleText.length > 0 ? scheduleText.join(', ') : doctor.practiceDays.join(', ');
    }
    
    return doctor.practiceDays.join(', ');
  };

  const formatDetailedSchedule = (doctor) => {
    if (!doctor.practiceDays || doctor.practiceDays.length === 0) {
      return [];
    }
    
    // If we have detailed schedule data, use it
    if (doctor.detailedSchedule && Object.keys(doctor.detailedSchedule).length > 0) {
      const scheduleDays = [];
      
      Object.keys(doctor.detailedSchedule).forEach(day => {
        const daySchedule = doctor.detailedSchedule[day];
        const amTime = daySchedule.AM || '';
        const pmTime = daySchedule.PM || '';
        
        if (amTime || pmTime) {
          const timeSlots = [];
          if (amTime) {
            const amFormatted = new Date(`2000-01-01T${amTime}`).toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            });
            timeSlots.push(amFormatted);
          }
          if (pmTime) {
            const pmFormatted = new Date(`2000-01-01T${pmTime}`).toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            });
            timeSlots.push(pmFormatted);
          }
          scheduleDays.push({
            day: day,
            times: timeSlots.join(', ')
          });
        }
      });
      
      return scheduleDays;
    }
    
    // Fallback to basic schedule
    const days = doctor.practiceDays;
    const time = doctor.practiceTime?.start && doctor.practiceTime?.end 
      ? `${doctor.practiceTime.start} - ${doctor.practiceTime.end}`
      : '';
    
    return days.map(day => ({
      day: day,
      times: time || 'Time not specified'
    }));
  };

  const formatScheduleForCard = (doctor) => {
    const schedule = formatDetailedSchedule(doctor);
    if (schedule.length === 0) {
      return 'Schedule not specified';
    }
    
    // Day abbreviation mapping for card display
    const dayAbbreviations = {
      'Monday': 'Mon',
      'Tuesday': 'Tue', 
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    
    return schedule.map(item => {
      const abbreviatedDay = dayAbbreviations[item.day] || item.day;
      return `${abbreviatedDay}: ${item.times}`;
    }).join('\n');
  };

  const formatScheduleForCardBoxes = (doctor) => {
    const schedule = formatDetailedSchedule(doctor);
    if (schedule.length === 0) {
      return [];
    }
    
    // Day abbreviation mapping for card display
    const dayAbbreviations = {
      'Monday': 'Mon',
      'Tuesday': 'Tue', 
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    
    return schedule.map(item => ({
      day: dayAbbreviations[item.day] || item.day,
      times: item.times
    }));
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(doctor);
    } else {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    // Handle action (email, call, etc.)
    console.log(`${action} clicked for ${doctor.name}`);
  };

  // Card styles based on variant
  const getCardStyles = () => {
    const baseStyles = {
      height: '100%',
      cursor: 'pointer',
      '&:hover': { 
        transform: 'translateY(-8px)', 
        transition: 'transform 0.3s ease',
        boxShadow: 6
      }
    };

    switch (variant) {
      case 'home':
        return {
          ...baseStyles,
          textAlign: 'center',
          overflow: 'hidden',
          '&:hover': { 
            transform: 'translateY(-8px)', 
            transition: 'all 0.3s ease',
            boxShadow: '0px 20px 40px rgba(0,0,0,0.1)',
          }
        };
      case 'department':
        return {
          ...baseStyles,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': { 
            transform: 'translateY(-4px)', 
            transition: 'transform 0.3s ease',
            boxShadow: 4
          }
        };
      default:
        return baseStyles;
    }
  };

  // Card content based on variant
  const renderCardContent = () => {
    switch (variant) {
      case 'home':
        return (
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
              {doctor.name}
            </Typography>
            <Chip 
              label={doctor.specialty} 
              color="primary" 
              sx={{ 
                mb: 3,
                fontWeight: 500,
                px: 2,
              }} 
            />
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {doctor.bio}
            </Typography>
          </CardContent>
        );
      
      case 'department':
        return (
          <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {doctor.name}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Chip 
                  label={doctor.specialty} 
                  color="primary" 
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SchoolIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', textAlign: 'center' }}>
                {doctor.education}
              </Typography>
            </Box>
          </CardContent>
        );
      
      default:
        return (
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              {doctor.name}
            </Typography>
            <Chip 
              label={doctor.specialty} 
              color="primary" 
              sx={{ mb: 2 }}
            />
            <Typography 
              variant="body2" 
              color="text.secondary" 
              paragraph
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(doctor.bio) }}
            />
            
            {doctor.showEducation !== false && doctor.education && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SchoolIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {doctor.education}
                </Typography>
              </Box>
            )}
            
            {doctor.showExperience !== false && doctor.experience && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {doctor.experience} experience
                </Typography>
              </Box>
            )}

            {doctor.showScheduleText !== false && doctor.scheduleText && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary', mt: 0.2 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(doctor.scheduleText) }}
                  />
                </Box>
              </Box>
            )}

            {doctor.showScheduleTime === true && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary', mt: 0.2 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Practice Schedule:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formatScheduleForCardBoxes(doctor).map((scheduleItem, index) => (
                      <Box
                        key={index}
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 'medium',
                          width: 'calc(50% - 4px)',
                          minWidth: '140px'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'medium' }}>
                          {scheduleItem.day}: {scheduleItem.times}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            {doctor.showEmailPhone !== false && showActions && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {doctor.email && (
                  <Button
                    size="small"
                    startIcon={<EmailIcon />}
                    sx={{ textTransform: 'none' }}
                    onClick={(e) => handleActionClick(e, 'email')}
                  >
                    Email
                  </Button>
                )}
                {doctor.phone && (
                  <Button
                    size="small"
                    startIcon={<PhoneIcon />}
                    sx={{ textTransform: 'none' }}
                    onClick={(e) => handleActionClick(e, 'call')}
                  >
                    Call
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        );
    }
  };

  // Image height based on variant
  const getImageHeight = () => {
    switch (variant) {
      case 'home':
        return 280;
      case 'department':
        return 250;
      default:
        return 300;
    }
  };

  return (
    <>
      <Card 
        sx={getCardStyles()}
        onClick={handleCardClick}
      >
         <CardMedia
           component="img"
           height={getImageHeight()}
           image={imageUrl || getGenderAvatar(doctor.gender)}
           alt={doctor.name}
           sx={{ 
             objectFit: 'cover',
             backgroundColor: imageLoading ? '#f0f0f0' : 'transparent',
             objectPosition: 'center top'
           }}
           onError={(e) => {
             e.target.src = getGenderAvatar(doctor.gender);
           }}
         />
        {renderCardContent()}
      </Card>

      {/* Doctor Details Modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Doctor Image */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={imageUrl || getGenderAvatar(doctor.gender)}
                  alt={doctor.name}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    maxHeight: '400px',
                    objectFit: 'cover',
                    backgroundColor: imageLoading ? '#f0f0f0' : 'transparent'
                  }}
                  onError={(e) => {
                    e.target.src = getGenderAvatar(doctor.gender);
                  }}
                />
              </Box>
            </Grid>

            {/* Doctor Details */}
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                About {doctor.name}
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(doctor.bio) }}
              />

              <Divider sx={{ my: 2 }} />

              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Department"
                    secondary={doctor.specialty}
                    primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                    secondaryTypographyProps={{ fontSize: '0.9rem' }}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      p: 1,
                      borderRadius: 1,
                      '& .MuiListItemText-primary': {
                        color: 'white',
                        fontWeight: 'bold'
                      },
                      '& .MuiListItemText-secondary': {
                        color: 'white',
                        opacity: 0.9
                      }
                    }}
                  />
                </ListItem>
                {doctor.showExperience !== false && doctor.experience && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <WorkIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Experience"
                      secondary={`${doctor.experience} years of experience`}
                      primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                )}

                {doctor.showEducation !== false && doctor.education && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <SchoolIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Education"
                      secondary={doctor.education}
                      primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                )}

                {doctor.showEmailPhone !== false && doctor.email && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={doctor.email}
                      primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                )}

                {doctor.showEmailPhone !== false && doctor.phone && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone"
                      secondary={doctor.phone}
                      primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                )}

                {doctor.showScheduleText !== false && doctor.scheduleText && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <ScheduleIcon color="primary" />
                    </ListItemIcon>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 1 }}>
                        Schedule
                      </Typography>
                      <Typography 
                        variant="body2" 
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(doctor.scheduleText) }}
                        sx={{ fontSize: '0.9rem' }}
                      />
                    </Box>
                  </ListItem>
                )}

                {doctor.showScheduleTime === true && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <ScheduleIcon color="primary" />
                    </ListItemIcon>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 1 }}>
                        Practice Schedule
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {formatDetailedSchedule(doctor).map((scheduleItem, index) => (
                          <Box
                            key={index}
                            sx={{
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText',
                              px: 2,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.7rem',
                              fontWeight: 'medium',
                              width: 'calc(33.333% - 8px)', // 3 items per row
                              minWidth: '140px',
                              flexBasis: 'calc(33.333% - 8px)' // Ensure 3 per row
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                              {scheduleItem.day}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                              {scheduleItem.times}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </ListItem>
                )}
              </List>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseModal}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DoctorCard; 